import BigNumber from 'bignumber.js'
import poolsConfig from 'config/constants/pools'
import sousChefABI from 'config/abi/sousChef.json'
import cakeABI from 'config/abi/cake.json'
import wbnbABI from 'config/abi/weth.json'
import multicall from 'utils/multicall'
import { getAddress } from 'utils/addressHelpers'
import { BIG_ZERO } from 'utils/bigNumber'
import { getSouschefV2Contract } from 'utils/contractHelpers'
import tokens from 'config/constants/tokens'
import { PoolCategory } from 'config/constants/types'

export const fetchPoolsBlockLimits = async () => {
  const poolsWithEnd = poolsConfig.filter(
    (p) => p.sousId !== 0 && p.poolCategory !== PoolCategory.AUTO
  )

  const callsStartBlock = poolsWithEnd.map((poolConfig) => ({
    address: getAddress(poolConfig.contractAddress),
    name: 'startBlock',
  }))

  const callsEndBlock = poolsWithEnd.map((poolConfig) => ({
    address: getAddress(poolConfig.contractAddress),
    name: 'bonusEndBlock',
  }))

  const starts = await multicall(sousChefABI, callsStartBlock)
  const ends = await multicall(sousChefABI, callsEndBlock)

  return poolsWithEnd.map((pool, index) => ({
    sousId: pool.sousId,
    startBlock: new BigNumber(starts[index] ?? '0').toJSON(),
    endBlock: new BigNumber(ends[index] ?? '0').toJSON(),
  }))
}

export const fetchPoolsTotalStaking = async () => {
  const nonBnbPools = poolsConfig.filter(
    (p) => p.stakingToken.symbol !== 'BNB' && p.poolCategory !== PoolCategory.AUTO
  )

  const bnbPools = poolsConfig.filter(
    (p) => p.stakingToken.symbol === 'MONEY' && p.poolCategory !== PoolCategory.CORE
  )

  const callsNonBnbPools = nonBnbPools.map((poolConfig) => ({
    address: getAddress(poolConfig.contractAddress),
    name: 'userStaked',
  }))

  const callsBnbPools = bnbPools.map((poolConfig) => ({
    address: tokens.wbnb.address,
    name: 'balanceOf',
    params: [getAddress(poolConfig.contractAddress)],
  }))

  const [nonBnbResult, bnbResult] = await Promise.all([
    multicall(sousChefABI, callsNonBnbPools),
    multicall(wbnbABI, callsBnbPools),
  ])

  const safeGet = (arr: any[], index: number) => {
    const val = arr[index]
    return val !== undefined && val !== null ? new BigNumber(val.toString()) : BIG_ZERO
  }

  return [
    ...nonBnbPools.map((p, index) => ({
      sousId: p.sousId,
      totalStaked: safeGet(nonBnbResult, index).toJSON(),
    })),
    ...bnbPools.map((p, index) => ({
      sousId: p.sousId,
      totalStaked: safeGet(bnbResult, index).toJSON(),
    })),
  ]
}

export const fetchPoolStakingLimit = async (
  sousId: number
): Promise<BigNumber> => {
  try {
    const sousContract = getSouschefV2Contract(sousId)
    const stakingLimit = await sousContract.poolLimitPerUser()
    return new BigNumber(stakingLimit.toString())
  } catch {
    return BIG_ZERO
  }
}

export const fetchPoolsStakingLimits = async (
  poolsWithStakingLimit: number[]
): Promise<{ [key: string]: BigNumber }> => {
  const validPools = poolsConfig
    .filter(
      (p) =>
        p.stakingToken.symbol !== 'MONEY' &&
        !p.isFinished &&
        p.poolCategory !== PoolCategory.AUTO
    )
    .filter((p) => !poolsWithStakingLimit.includes(p.sousId))

  const stakingLimitPromises = validPools.map((validPool) =>
    fetchPoolStakingLimit(validPool.sousId)
  )
  const stakingLimits = await Promise.all(stakingLimitPromises)

  return stakingLimits.reduce((accum, stakingLimit, index) => {
    return {
      ...accum,
      [validPools[index].sousId]: stakingLimit,
    }
  }, {})
}
